import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n, lifeAreaLabels } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Check, ArrowRight, ArrowLeft, Sparkles, HeartPulse, Users, Home, Wallet,
  FileText, Shield, Monitor, ScrollText,
} from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";

const AREA_ICONS = {
  gesundheit: HeartPulse,
  familie: Users,
  zuhause: Home,
  finanzen: Wallet,
  dokumente: FileText,
  vorsorge: Shield,
  digital: Monitor,
  nachlass: ScrollText,
  wuensche: Sparkles,
};

const STORAGE_KEY = "ag_onboarding_progress_v1";

export default function OnboardingPage() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { refresh, user } = useAuth();
  const [step, setStep] = useState(0);
  const [priorities, setPriorities] = useState([]);
  const [contact, setContact] = useState({ name: "", relationship: "", phone: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [animKey, setAnimKey] = useState(0);
  const [hydrated, setHydrated] = useState(false);

  const labels = lifeAreaLabels[lang] || lifeAreaLabels.de;
  const areas = ["gesundheit", "familie", "dokumente", "zuhause", "finanzen", "vorsorge", "digital"];

  // Hydration: aus localStorage Fortschritt wiederherstellen
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (typeof saved.step === "number") setStep(saved.step);
        if (Array.isArray(saved.priorities)) setPriorities(saved.priorities);
        if (saved.contact) setContact({ name: "", relationship: "", phone: "", email: "", ...saved.contact });
      }
    } catch { /* ignore */ }
    setHydrated(true);
  }, []);

  // Persist Fortschritt
  useEffect(() => {
    if (!hydrated) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ step, priorities, contact }));
    } catch { /* ignore */ }
  }, [step, priorities, contact, hydrated]);

  const togglePriority = (key) =>
    setPriorities((p) => (p.includes(key) ? p.filter((x) => x !== key) : [...p, key]));

  const goto = (next) => {
    setAnimKey((k) => k + 1);
    setStep(next);
  };

  const finish = async () => {
    setLoading(true);
    try {
      await api.post("/onboarding/complete", { priorities, completed: true });
      if (contact.name && contact.relationship) {
        await api.post("/emergency-contacts", {
          name: contact.name,
          relationship: contact.relationship,
          phone: contact.phone,
          email: contact.email,
          access_allowed: false,
        });
      }
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      await refresh();
      toast.success(lang === "de" ? "Schön, dass Sie da sind." : "Glad you're here.");
      navigate("/dashboard");
    } catch {
      toast.error(lang === "de" ? "Konnte Onboarding nicht abschließen." : "Could not finish onboarding.");
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    {
      title: t.onboarding.step1Title,
      sub: t.onboarding.step1Sub,
      body: (
        <div className="flex flex-col items-center text-center pt-2">
          <div className="ag-onb-emblem mb-8">
            <Sparkles className="h-7 w-7 text-emerald-700" strokeWidth={1.4} />
          </div>
          <p className="font-serif text-2xl md:text-[26px] text-ag-ink max-w-md leading-snug">
            {user?.first_name
              ? (lang === "de" ? `Hallo ${user.first_name}.` : `Hello ${user.first_name}.`)
              : (lang === "de" ? "Hallo." : "Hello.")}{" "}
            {lang === "de" ? "Wir gehen das gemeinsam an." : "We will take this on together."}
          </p>
        </div>
      ),
    },
    {
      title: t.onboarding.step2Title,
      sub: t.onboarding.step2Sub,
      body: (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-2">
          {areas.map((k, idx) => {
            const Icon = AREA_ICONS[k] || Sparkles;
            const active = priorities.includes(k);
            return (
              <button
                key={k}
                type="button"
                data-testid={`onb-priority-${k}`}
                onClick={() => togglePriority(k)}
                style={{ animationDelay: `${idx * 40}ms` }}
                className={`ag-fade-in-up p-4 rounded-2xl border text-left transition-all ${
                  active ? "border-ag-ink bg-ag-ink text-white" : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <Icon className={`h-5 w-5 mb-3 ${active ? "text-amber-300" : "text-slate-500"}`} strokeWidth={1.5} />
                <div className="font-serif text-lg leading-tight">{labels[k]}</div>
                {active && <Check className="h-4 w-4 mt-2 text-amber-300" />}
              </button>
            );
          })}
        </div>
      ),
    },
    {
      title: t.onboarding.step3Title,
      sub: t.onboarding.step3Sub,
      body: (
        <div className="pt-2 space-y-3 max-w-md">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">{t.emergency.name}</Label>
            <Input value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} data-testid="onb-contact-name" className="mt-1.5 h-11 bg-white" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">{t.emergency.relationship}</Label>
            <Input value={contact.relationship} onChange={(e) => setContact({ ...contact, relationship: e.target.value })} data-testid="onb-contact-rel" className="mt-1.5 h-11 bg-white" placeholder={lang === "de" ? "z. B. Ehepartnerin" : "e.g. spouse"} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t.emergency.phone}</Label>
              <Input value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} data-testid="onb-contact-phone" className="mt-1.5 h-11 bg-white" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">{t.emergency.email}</Label>
              <Input type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} data-testid="onb-contact-email" className="mt-1.5 h-11 bg-white" />
            </div>
          </div>
          <button
            type="button" data-testid="onb-skip-contact"
            onClick={() => goto(step + 1)}
            className="text-xs text-slate-500 underline-offset-4 hover:underline"
          >{t.onboarding.step3Skip}</button>
        </div>
      ),
    },
    {
      title: t.onboarding.step4Title,
      sub: t.onboarding.step4Sub,
      body: (
        <div className="text-center pt-2">
          <div className="ag-onb-emblem ag-onb-emblem--gold mx-auto mb-5">
            <Check className="h-7 w-7 text-amber-700" />
          </div>
          <p className="font-serif text-2xl text-ag-ink max-w-md mx-auto">
            {lang === "de" ? "Schritt für Schritt. Sie sind sicher hier." : "Step by step. You are safe here."}
          </p>
        </div>
      ),
    },
  ];

  const current = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <div className="min-h-screen bg-ag-paper ag-paper-grain flex items-center justify-center p-6">
      <div className="w-full max-w-2xl ag-card p-8 md:p-12 ag-fade-in">
        <div className="flex justify-center mb-8">
          <Logo variant="full" tone="light" className="ag-logo-enter h-14 w-auto" />
        </div>
        <div className="flex items-center gap-1.5 mb-8">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1 rounded-full transition-all duration-700 ease-out ${
                i < step ? "bg-emerald-500 w-8"
                  : i === step ? "bg-ag-ink w-10"
                  : "bg-slate-200 w-4"
              }`}
            />
          ))}
        </div>

        <div key={animKey} className="ag-onb-step">
          <h1 className="font-serif text-4xl text-ag-ink mb-3" data-testid="onb-title">{current.title}</h1>
          <p className="text-slate-600 max-w-lg leading-relaxed">{current.sub}</p>
          <div className="mt-6">{current.body}</div>
        </div>

        <div className="flex items-center justify-between mt-10 pt-6 border-t border-slate-100">
          <button
            type="button" onClick={() => goto(Math.max(0, step - 1))}
            disabled={step === 0}
            data-testid="onb-back"
            className="text-sm text-slate-500 hover:text-ag-ink disabled:opacity-30 inline-flex items-center gap-1.5"
          ><ArrowLeft className="h-3.5 w-3.5" /> {lang === "de" ? "Zurück" : "Back"}</button>
          {!isLast ? (
            <Button onClick={() => goto(step + 1)} data-testid="onb-next" className="bg-ag-ink hover:bg-slate-800 h-11 rounded-xl">
              {t.onboarding.continue} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={finish} disabled={loading} data-testid="onb-finish" className="bg-emerald-600 hover:bg-emerald-700 h-11 rounded-xl">
              {t.onboarding.finish} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>

        <p className="text-[11px] text-slate-400 text-center mt-6">
          {lang === "de"
            ? "Ihr Fortschritt wird automatisch gespeichert."
            : "Your progress is saved automatically."}
        </p>
      </div>
    </div>
  );
}
